import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { WEEKLY_REPORT_EXAMPLE as R } from '../data/site';
import { useInView } from '../hooks/useInView';
import { DemoDescription, ExampleTag, OfferDemoHandle, ReplayButton } from './OfferDemoShared';

/** Pulled from the same weekly-report example the hero uses, so the numbers never disagree. */
const NEW_LEADS = R.stats.find((s) => s.label === 'New leads');
const COST_PER_LEAD = R.stats.find((s) => s.label === 'Cost per lead');
const LINES = [
  { label: NEW_LEADS?.label ?? 'New leads', value: NEW_LEADS?.value ?? '23' },
  { label: COST_PER_LEAD?.label ?? 'Cost per lead', value: COST_PER_LEAD?.value ?? '$28' },
  { label: 'Next', value: 'Move $200 to Instagram' },
];

/** notification phase, then each report line, in ms from the start of the sequence */
const NOTIFY_AT = 60;
const EXPAND_AT = 420;
const LINE_STEP = 90;

/**
 * What the "Report" offer card actually does: the Friday notification a
 * client actually gets, then the one-page report it opens into. Plays
 * once, in view, then holds on the filled-in report.
 */
const OfferDemoReport = forwardRef<OfferDemoHandle, object>((_props, ref) => {
  const reduced = useReducedMotion();
  const { ref: viewRef, inView } = useInView<HTMLDivElement>(0.4);
  const started = useRef(false);
  const [phase, setPhase] = useState<'idle' | 'notify' | 'report'>(reduced ? 'report' : 'idle');
  const [linesShown, setLinesShown] = useState(reduced ? LINES.length : 0);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };

  const play = useCallback(() => {
    if (reduced) return;
    clearTimers();
    setPhase('idle');
    setLinesShown(0);
    timers.current.push(window.setTimeout(() => setPhase('notify'), NOTIFY_AT));
    timers.current.push(window.setTimeout(() => setPhase('report'), EXPAND_AT));
    LINES.forEach((_, i) => {
      timers.current.push(
        window.setTimeout(() => setLinesShown((n) => Math.max(n, i + 1)), EXPAND_AT + 80 + i * LINE_STEP)
      );
    });
  }, [reduced]);

  useImperativeHandle(ref, () => ({ replay: play }));

  useEffect(() => {
    if (reduced || started.current || !inView) return;
    started.current = true;
    play();
  }, [reduced, inView, play]);

  useEffect(() => () => clearTimers(), []);

  const ended = phase === 'report' && linesShown >= LINES.length;

  return (
    <div className="offerdemo offerdemo--report" ref={viewRef}>
      <DemoDescription>
        {`Example: a Friday notification opens into a one-page report for ${R.client}: ${LINES.map((l) => `${l.label} ${l.value}`).join(', ')}.`}
      </DemoDescription>
      <ExampleTag />
      <div className="offerdemo__mailwrap" aria-hidden="true">
        {phase === 'notify' && (
          <div className="offerdemo__mail">
            <p className="offerdemo__mailsubj">Your week at {R.client}</p>
            <p className="offerdemo__mailtime">{R.approvedAt}</p>
          </div>
        )}
        {phase === 'report' && (
          <div className="offerdemo__report">
            <p className="offerdemo__reporthead">{R.client}</p>
            {LINES.map((line, i) => (
              <p
                className={`offerdemo__reportline${i < linesShown ? ' is-shown' : ''}`}
                key={line.label}
              >
                <span>{line.label}</span>
                <b>{line.value}</b>
              </p>
            ))}
          </div>
        )}
      </div>
      <ReplayButton what="report" visible={!reduced && ended} onClick={play} />
    </div>
  );
});

OfferDemoReport.displayName = 'OfferDemoReport';

export default React.memo(OfferDemoReport);
